import { useEffect } from 'react';
import { ReactComponent as Arrow } from '@assets/icons/arrowForward.svg';
import { SERVICES_TABS } from 'constant';

import { Box, Collapse, Stack, Text, useTheme } from '@chakra-ui/react';

import { CategoryInterface, SpecialServicesTabsIds } from 'types';

interface StepperProps {
  categories: CategoryInterface[];
  specialCategories: CategoryInterface[];
  currentCategory: CategoryInterface;
  onChangeCategory: (category: CategoryInterface) => void;
  showSummaryTab: boolean;
  playAddingAnimation: boolean;
}

const ServiceStepper = ({
  categories,
  specialCategories,
  currentCategory,
  onChangeCategory,
  showSummaryTab,
  playAddingAnimation
}: StepperProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: { stepper }
        }
      }
    }
  } = useTheme();
  const reviewTab = SERVICES_TABS[SpecialServicesTabsIds.Review];
  const isReviewStep = currentCategory.id === reviewTab.id;
  const isAlternativeReviewTab = playAddingAnimation || isReviewStep;
  const lastSpecialCategory = specialCategories?.[specialCategories.length - 1];
  const consultationCategoryName =
    SERVICES_TABS[SpecialServicesTabsIds.Consultation].name.toLowerCase();
  const isConsultationCategoryIncluded = categories.some(
    (category) => category.name.toLowerCase() === consultationCategoryName
  );
  const categoryExtraSpaceName = isConsultationCategoryIncluded
    ? consultationCategoryName
    : lastSpecialCategory?.name.toLowerCase();

  const onChangeHandler = (value: CategoryInterface) => () => {
    onChangeCategory(value);
  };

  useEffect(() => {
    if (showSummaryTab) {
      onChangeCategory(reviewTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack gap='3px' margin={0}>
      <Collapse
        in={showSummaryTab}
        style={{
          width: '100%',
          overflow: isReviewStep ? 'visible' : 'hidden'
        }}
      >
        <Box pb='7px' w='100%'>
          <Box
            onClick={onChangeHandler(reviewTab)}
            display='flex'
            alignItems='center'
            position='relative'
            padding='13px 17px'
            borderLeftRadius='3px'
            borderRightRadius={isReviewStep ? 0 : '3px'}
            mr={isReviewStep ? '-5px' : 0}
            w={isReviewStep ? 'calc(100% + 5px)' : '100%'}
            bg={
              isAlternativeReviewTab
                ? stepper.reviewStepActiveBg
                : stepper.reviewStepInactiveBg
            }
            cursor='pointer'
            opacity={1}
            transition={
              playAddingAnimation
                ? 'opacity 0.75s, background 0.75s'
                : 'opacity 1.25s, background 1.25s'
            }
            pr='35px'
          >
            <Text
              fontSize='14px'
              textTransform='uppercase'
              color={
                isAlternativeReviewTab
                  ? stepper.reviewStepActiveText
                  : stepper.reviewStepInactiveText
              }
              noOfLines={2}
            >
              {playAddingAnimation ? 'Added to booking' : "What I'm booking"}
            </Text>
            {isReviewStep && (
              <Box position='absolute' right='15px'>
                <Arrow
                  width={14}
                  height={14}
                  fill={
                    isAlternativeReviewTab
                      ? stepper.reviewStepActiveText
                      : stepper.reviewStepInactiveText
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>

      {categories.map((tab) => {
        const activeTab = tab.id === currentCategory.id;
        const shouldUseBottomSpace =
          tab.name.toLowerCase() === categoryExtraSpaceName;

        return (
          <Box
            key={tab.id}
            onClick={onChangeHandler(tab)}
            display='flex'
            alignItems='center'
            position='relative'
            padding='13px 17px'
            borderLeftRadius='3px'
            borderRightRadius={activeTab ? 0 : '3px'}
            mr={activeTab ? '-5px' : 0}
            w={activeTab ? 'calc(100% + 5px)' : '100%'}
            bg={activeTab ? stepper.activeBg : stepper.inactiveBg}
            cursor='pointer'
            opacity={1}
            transition='opacity .2s'
            mb={shouldUseBottomSpace ? '7px' : 0}
            pr='35px'
          >
            <Text
              textTransform='uppercase'
              fontSize='14px'
              fontWeight={activeTab ? 'bold' : 'regular'}
              color={activeTab ? stepper.activeText : stepper.inactiveText}
              noOfLines={2}
            >
              {tab.name}
            </Text>
            {activeTab && (
              <Box position='absolute' right='15px'>
                <Arrow width={14} height={14} fill='white' />
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default ServiceStepper;
